/* ============================================================================
 * Family Set — simple, readable vanilla JS
 * - HTML already contains one .family-set__set block.
 * - "Add another set" clones that block and re-binds listeners.
 * - Bundle state is in memory only (no visible summary).
 * ========================================================================== */

const FamilySet = (() => {
  // --- Accessibility live region -------------------------------------------
  let liveRegion;
  function ensureLiveRegion() {
    if (!liveRegion) {
      liveRegion = document.getElementById('fs-live');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'fs-live';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.className = 'visually-hidden';
        document.body.appendChild(liveRegion);
      }
    }
  }

  function announce(msg) {
    ensureLiveRegion();
    if (liveRegion) {
      liveRegion.textContent = '';
      // Delay helps some SRs repeat announcements
      setTimeout(() => { liveRegion.textContent = msg; }, 10);
    }
  }

  const bundle = { items: [] };

  function removeVariantFromBundle(variantId) {
    const before = bundle.items.length;
    bundle.items = bundle.items.filter(i => String(i.id) !== String(variantId));
    updateBundleCtaTotal();
  }

  // --- Bundle helpers --------------------------------------------------------
  function mergeEditOrPush(item) {
    if (item.setNumber) {
      const idxBySet = bundle.items.findIndex(i => String(i.setNumber) === String(item.setNumber));
      if (idxBySet !== -1) {
        bundle.items[idxBySet] = item;
        return;
      }
    }

    const existing = bundle.items.find(i => String(i.id) === String(item.id));
    if (existing) {
      existing.quantity = parseInt(existing.quantity, 10) + parseInt(item.quantity, 10);
      existing.price = item.price; // update price if changed
    } else {
      bundle.items.push(item);
    }
    updateBundleCtaTotal();
  }

  // --- CTA total updater -----------------------------------------------------
  function updateBundleCtaTotal() {
    const ctaButton = document.querySelector('.family-set__add-bundle-to-bag');
    if (!ctaButton) return;
    const totalPriceElement = ctaButton.querySelector('.family-set__bundle-total');
    const priceSeparatorElement = ctaButton.querySelector('.family-set__bundle-separator');

    // Sum current bundle items
    let total = 0;
    let currencySymbol = '';
    bundle.items.forEach((bundleItem) => {
      if (!currencySymbol) {
        const priceSource = bundleItem.totalPrice || bundleItem.unitPrice || '';
        currencySymbol = priceSource.replace(/[^£$€¥]/g, '') || '£';
      }
      const bundleItemTotal =
        parseFloat(String(bundleItem.totalPrice || '').replace(/[^0-9.]/g, '')) ||
        (parseFloat(String(bundleItem.unitPrice || '').replace(/[^0-9.]/g, '')) *
          (parseInt(bundleItem.quantity, 10) || 0)) ||
        0;
      total += isNaN(bundleItemTotal) ? 0 : bundleItemTotal;
    });

    if (bundle.items.length > 0) {
      const formatted = `${currencySymbol || '£'}${(total % 1 === 0 ? total.toFixed(0) : total.toFixed(2))}`;
      if (totalPriceElement) totalPriceElement.textContent = formatted;
      if (priceSeparatorElement) priceSeparatorElement.classList.remove('hidden');
    } else {
      if (totalPriceElement) totalPriceElement.textContent = '';
      if (priceSeparatorElement) priceSeparatorElement.classList.add('hidden');
    }
  }

  // --- Step 1: Select product tile -------------------------------------------
  function handleStep1Select(setRoot, tileBtn) {
    const productId = tileBtn.getAttribute('data-product-id');
    if (!productId) return;

    // remove existing active classes
    setRoot.querySelectorAll('.family-set__item.active')
      .forEach(el => el.classList.remove('active'));
    tileBtn.classList.add('active');

    // hide all step2 instantly (prevent flicker)
    setRoot.querySelectorAll('.family-set__step-2')
      .forEach(el => {
        el.classList.remove('active', 'fade-in');
        el.style.display = 'none';
      });

    // show matching step2 with optional fade-in
    const step2El = setRoot.querySelector(`.family-set__step-2[data-product-id="${productId}"]`);
    if (step2El) {
      step2El.style.display = '';
      step2El.classList.add('active');

      if (!step2El.dataset.initialized) {
        step2El.classList.add('fade-in');
        step2El.dataset.initialized = 'true';
        step2El.addEventListener('transitionend', () => {
          step2El.classList.remove('fade-in');
        }, { once: true });
      }
    }

    // enable step-2 header for this product
    const step2Header = setRoot.querySelector(`.family-set__step-2-header`);
    if (step2Header) step2Header.classList.remove('disabled');

    // hide footer until size chosen for this product
    const footer = setRoot.querySelector('.family-set__footer');
    if (footer) footer.classList.add('hidden');

  }

  // --- Step 2: Select size ---------------------------------------------------
  function handleSizeSelect(step2El, sizeBtn) {
    step2El.querySelectorAll('.family-set__size-item.active')
      .forEach(el => el.classList.remove('active'));
    sizeBtn.classList.add('active');

    // update price display
    const price = sizeBtn.getAttribute('data-price');
    if (price) {
      const priceSpan = step2El.closest('.family-set__set-container').querySelector('.family-set__set-price');
      if (priceSpan) priceSpan.innerHTML = price;
      // also update total-price span in header live
      const headerPriceSpan = step2El.closest('.family-set').querySelector('.family-set__set-total-price');
      if (headerPriceSpan && !headerPriceSpan.classList.contains('hidden')) {
        headerPriceSpan.innerHTML = `&nbsp;- ${price}`;
      }
    }

    // show footer now that size selected
    const footer = step2El.closest('.family-set__set-container').querySelector('.family-set__footer');
    if (footer) footer.classList.remove('hidden');
  }

  // --- Collect and add configured line --------------------------------------
  function collectConfiguredLine(step2El) {
    const sizeBtn = step2El.querySelector('.family-set__size-item.active');
    if (!sizeBtn) {
      console.warn('[FamilySet] No size selected');
      return null;
    }

    const variantId = sizeBtn.getAttribute('data-variant-id');
    const qtyInput = step2El.closest('.family-set__set-container').querySelector('.family-set__set-quantity-input');
    const quantity = parseInt(qtyInput && qtyInput.value, 10) || 1;

    const activeSizeBtn = step2El.querySelector('.family-set__size-item.active');
    const unitPriceText = activeSizeBtn ? (activeSizeBtn.getAttribute('data-price') || '') : '';
    const currencySymbol = unitPriceText.replace(/[^£$€¥]/g, '') || '£';
    const numericPrice = parseFloat(unitPriceText.replace(/[^0-9.]/g, '')) || 0;
    const totalVal = numericPrice * quantity;
    const formattedTotal = `${currencySymbol}${(totalVal % 1 === 0 ? totalVal.toFixed(0) : totalVal.toFixed(2))}`;

    const productId = step2El.getAttribute('data-product-id') || '';

    if (!variantId) return null;
    return { id: variantId, quantity, productId, unitPrice: unitPriceText, totalPrice: formattedTotal };
  }

  function addConfiguredLineToBundle(step2El) {
    const line = collectConfiguredLine(step2El);
    if (!line) return;

    // Re-read quantity and price from DOM to ensure latest values
    const qtyInputLive = step2El.closest('.family-set__set-container').querySelector('.family-set__set-quantity-input');
    const latestQty = Number(qtyInputLive?.value) || line.quantity;
    line.quantity = latestQty;

    const unitPriceTextLive = step2El.closest('.family-set__set-container')
      .querySelector('.family-set__size-item.active')?.getAttribute('data-price') || '';
    const currencySymbolLive = unitPriceTextLive.replace(/[^£$€¥]/g, '') || '£';
    const numericLive = parseFloat(unitPriceTextLive.replace(/[^0-9.]/g, '')) || 0;
    const totalValLive = numericLive * latestQty;
    line.totalPrice = `${currencySymbolLive}${(totalValLive % 1 === 0 ? totalValLive.toFixed(0) : totalValLive.toFixed(2))}`;

    // Determine which set is being edited/added by reading the visible set number
    const familySetRoot = step2El.closest('.family-set');
    const setNumberEl = familySetRoot ? familySetRoot.querySelector('.family-set__set-number') : null;
    const setNumber = setNumberEl && setNumberEl.textContent ? setNumberEl.textContent.trim() : '';
    if (setNumber) {
      line.setNumber = setNumber;
    }

    mergeEditOrPush(line);
    updateAddAnotherBtn();
    announce('Set added to bundle');

    // Build summary string e.g., "2 x Slim Fit - £55"
    const quantity = line.quantity;
    // selected fit label = active tile text
    const activeTile = step2El.closest('.family-set__set-container').querySelector('.family-set__item.active')
      || step2El.closest('.family-set__set-container').querySelector('.family-set__item');
    const selectedFit = activeTile ? activeTile.textContent.trim() : '';
    // Fetch current displayed total price from footer (already quantity * unit)

    // Get variant title from active tile
    const variantTitle = step2El.closest('.family-set__set-container').querySelector('.family-set__size-item.active')?.getAttribute('data-variant-title') || '';
    const priceText = line.totalPrice || '';

    const summaryStr = `${quantity} x ${selectedFit}, ${variantTitle}`;

    // Update heading text inside same family-set root
    const headerH2 = (step2El.closest('.family-set')).querySelector('.family-set__header-title');
    if (headerH2) {
      const titleSpan = headerH2.querySelector('span[data-original-title]');
      const numSpan   = headerH2.querySelector('.family-set__set-number');
      const priceSpan = headerH2.querySelector('.family-set__set-total-price');

      if (titleSpan) titleSpan.textContent = summaryStr;

      // display price span
      if (priceSpan) {
        priceSpan.innerHTML = `&nbsp;- ${priceText}`;
        priceSpan.classList.remove('hidden');
      }

      // hide number span while in configured state
      if (numSpan) numSpan.classList.add('hidden');
    }

    // reset UI
    step2El.classList.remove('active');
    const setRoot = step2El.closest('.family-set__set-container');
    if (setRoot) {
      setRoot.querySelectorAll('.family-set__item.active')
        .forEach(el => el.classList.remove('active'));
      const familySetRoot = step2El.closest('.family-set');
      if (familySetRoot) {
        familySetRoot.classList.add('family-set--minimised');
      }

      // store chosen config on the container for future edits
      setRoot.dataset.variantId = line.id;
      setRoot.dataset.quantity = line.quantity;
      setRoot.dataset.productId = line.productId;
      if (setNumber) setRoot.dataset.setNumber = setNumber;
    }

    if (familySetRoot) {
      familySetRoot.classList.remove('family-set--update-set');
    }

    // After saving, move focus to Edit button in header for context
    const editBtnHeader = (step2El.closest('.family-set')).querySelector('.family-set__edit-set');
    if (editBtnHeader) {
      editBtnHeader.setAttribute('tabindex', '-1');
      editBtnHeader.focus();
    }
  }

  // --- Add bundle to Shopify cart -------------------------------------------
  function postBundleToCart(triggerBtn) {
    if (!bundle.items.length) {
      console.warn('[FamilySet] No items to add to bag');
      // show validation message near CTA
      const valEl = document.querySelector('.family-set__add-bundle-to-bag-error');
      if (valEl) {
        valEl.classList.remove('hidden');
        setTimeout(()=> valEl.classList.add('hidden'), 4000);
      }
      return;
    }

    triggerBtn.disabled = true;

    const payload = {
      items: bundle.items.map(i => ({
        id: parseInt(i.id, 10),
        quantity: parseInt(i.quantity, 10)
      })),
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: `${window.location.pathname}${window.location.search}`
    };

    fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload)
    })
      .then(r => r.json())
      .then(json => {

        // Publish cart update so other modules can react
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'family-set', cartData: json });
        }

        // Update count bubble
        if (window.updateCartItemCount) {
          window.updateCartItemCount();
        }

        // Render cart drawer if present
        const cartDrawer = document.querySelector('cart-drawer');
        if (
          cartDrawer &&
          typeof cartDrawer.renderContents === 'function' &&
          json.sections &&
          json.sections['cart-drawer'] &&
          json.sections['cart-drawer'].trim().length
        ) {
          try {
            cartDrawer.renderContents(json);
            // NEW: make absolutely sure the empty-state class is cleared
            cartDrawer.classList.remove('is-empty');
            announce('Bundle added to bag');
          } catch (err) {
            console.error('[FamilySet] cartDrawer.renderContents error', err);
            cartDrawer.open();
          }
        } else {
          // fallback: navigate to /cart or open existing drawer without update
          if (cartDrawer) {
            cartDrawer.open();
          }
        }
      })
      .catch(err => console.error('[FamilySet] Error', err))
      .finally(() => { triggerBtn.disabled = false; });
  }

  // --- Clone another set block -----------------------------------------------
  function cloneSetBlock(familySetRoot) {
    const firstSet = familySetRoot.querySelector('.family-set__set-container');
    if (!firstSet) return console.warn('[FamilySet] No .family-set__set-container to clone');

    const clone = firstSet.cloneNode(true);

    refreshUniqueIds(clone);

    // reset cloned state
    clone.querySelectorAll('.active').forEach(el => el.classList.remove('active'));
    clone.querySelectorAll('input[type="number"]').forEach(inp => {
      inp.value = inp.min || '1';
    });

    // Update overall numbering
    updateFamilySetNumbers();

    setupEventListenersForSet(clone);

    const host = familySetRoot.querySelector('.family-set__sets-host') || familySetRoot;
    host.appendChild(clone);
  }

  // --- Clone entire family-set block -----------------------------------------
  function cloneFamilySetBlock(familySetRoot) {
    const clone = familySetRoot.cloneNode(true);

    refreshUniqueIds(clone);

    // reset state of the cloned block
    clone.querySelectorAll('.active').forEach(el => el.classList.remove('active'));
    clone.querySelectorAll('input[type="number"]').forEach(inp => { inp.value = inp.min || '1'; });

    // hide footer initially
    clone.querySelector('.family-set__footer')?.classList.add('hidden');

    // Ensure the new clone starts expanded
    clone.classList.remove('family-set--minimised');
    // Insert clone before the global add-another button (at end of list)
    const globalAddBtn = document.querySelector('.family-set__add-another-set');
    if (globalAddBtn && globalAddBtn.parentNode) {
      globalAddBtn.parentNode.insertBefore(clone, globalAddBtn);
    } else {
      familySetRoot.parentNode.insertBefore(clone, familySetRoot.nextSibling);
    }

    // reset spans visibility and heading
    const totalSets = document.querySelectorAll('.family-set').length;

    const headingSpan = clone.querySelector('.family-set__header h2 span[data-original-title]');
    const cloneNum   = clone.querySelector('.family-set__set-number');
    const clonePrice = clone.querySelector('.family-set__set-total-price');

    if (headingSpan) headingSpan.textContent = headingSpan.getAttribute('data-original-title');
    if (cloneNum) {
      cloneNum.textContent = totalSets;
      cloneNum.classList.remove('hidden');
    }
    if (clonePrice) {
      clonePrice.textContent = '';
      clonePrice.classList.add('hidden');
    }

    // Attach listeners to the new block
    setupListenersForFamilySet(clone);
    // Re-run global numbering
    updateFamilySetNumbers();
  }

  // Helper to attach all listeners (per-set + top-level) for a given family-set root
  function setupListenersForFamilySet(familySetRoot) {
    familySetRoot.querySelectorAll('.family-set__set-container')
      .forEach(setRoot => setupEventListenersForSet(setRoot));

    // Add-bundle button listener
    familySetRoot.addEventListener('click', (e) => {
      const triggerBtn = e.target.closest('.family-set__add-bundle-to-bag');
      if (triggerBtn) {
        postBundleToCart(triggerBtn);
      }

      // remove-set at family root level (header button)
      const removeBtn = e.target.closest('.family-set__remove-set');
      if (removeBtn) {
        const setContainer = familySetRoot.querySelector('.family-set__set-container');
        if (setContainer) {
          // trigger existing reset logic set inside inner listener by dispatch
          setContainer.querySelectorAll('.family-set__remove-set').forEach(btn=>btn.classList.add('processed'));
          familySetRoot.classList.remove('family-set--minimised');

          const storedVariant = setContainer.dataset.variantId;
          if (storedVariant) {
            bundle.items = bundle.items.filter(l => String(l.id) !== String(storedVariant));
            updateBundleCtaTotal();
          }
          setContainer.querySelectorAll('.active').forEach(el=>el.classList.remove('active'));
          setContainer.querySelector('.family-set__footer')?.classList.add('hidden');

          const titleSpan = familySetRoot.querySelector('[data-original-title]');
          const priceSpan = familySetRoot.querySelector('.family-set__set-total-price');
          const numSpan   = familySetRoot.querySelector('.family-set__set-number');
          if (titleSpan) titleSpan.textContent = titleSpan.getAttribute('data-original-title');
          if (priceSpan) {
            priceSpan.textContent = '';
            priceSpan.classList.add('hidden');
          }
          if (numSpan) numSpan.classList.remove('hidden');

          delete setContainer.dataset.variantId;
          delete setContainer.dataset.quantity;
          delete setContainer.dataset.productId;
        }
      }
    });

    // Add-another-set (clone family-set) listener
    familySetRoot.addEventListener('click', (e) => {
      const addAnotherBtn = e.target.closest('.family-set__add-another-set');
      if (addAnotherBtn) {
        if (!bundle.items.length) {
          // check for half-configured set
          if (hasHalfConfiguredSet()) {
            document.querySelector('.family-set__update-set-error')?.classList.remove('hidden');
            setTimeout(()=>document.querySelector('.family-set__update-set-error')?.classList.add('hidden'),4000);
            return;
          } else {
            console.warn('[FamilySet] Bundle is empty, cannot clone another set');
            const valEl = addAnotherBtn.parentElement.querySelector('.family-set__another-set-error');
            if (valEl) {
              valEl.classList.remove('hidden');
              setTimeout(()=> valEl.classList.add('hidden'), 4000);
            }
          }
          return;
        }
        cloneFamilySetBlock(familySetRoot);
      }

      const editBtn = e.target.closest('.family-set__edit-set');
      if (editBtn) {
        // Reveal the set container & +another button again
        const setContainer = familySetRoot.querySelector('.family-set__set-container');
        if (setContainer) {
          if (familySetRoot.classList.contains('family-set--update-set')) {
            familySetRoot.classList.remove('family-set--update-set');
          }else{
            familySetRoot.classList.add('family-set--update-set');
          }
          familySetRoot.classList.remove('family-set--minimised');
          familySetRoot.classList.remove('family-set--closed');

          // restore previous selections for easier editing
          const { variantId, productId, quantity } = setContainer.dataset;
          if (productId) {
            const tile = familySetRoot.querySelector(`.family-set__item[data-product-id="${productId}"]`);
            if (tile) tile.classList.add('active');
          }
          if (variantId) {
            const sizeBtn = familySetRoot.querySelector(`.family-set__size-item[data-variant-id="${variantId}"]`);
            if (sizeBtn) {
              const step2 = sizeBtn.closest('.family-set__step-2');
              if (step2) step2.classList.add('active');
              sizeBtn.classList.add('active');
            }
          }
          if (quantity) {
            const qtyInput = familySetRoot.querySelector('.family-set__set-quantity input');
            if (qtyInput) qtyInput.value = quantity;
          }

          // Move focus to first interactive element for keyboard users
          const focusTarget = setContainer.querySelector('.family-set__item') || setContainer.querySelector('button, [role="button"], input');
          if (focusTarget) {
            focusTarget.setAttribute('tabindex', '-1');
            focusTarget.focus();
          }
        }

      }
    });

    // Header buttons listener (toggle, edit, remove)
    const headerBtns = familySetRoot.querySelector('.family-set__header-buttons');
    if (headerBtns) headerBtns.addEventListener('click', (ev) => {
      const toggleBtn = ev.target.closest('.family-set__toggle-set');
      const editBtn   = ev.target.closest('.family-set__edit-set');
      const removeBtn = ev.target.closest('.family-set__remove-set');

      if (toggleBtn) {
        familySetRoot.classList.toggle('family-set--closed');
        const expanded = !familySetRoot.classList.contains('family-set--closed');
        toggleBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        return;
      }

      const setContainer = familySetRoot.querySelector('.family-set__set-container');
      if (!setContainer) return;

      if (editBtn) {
        // reset block via existing logic
        setContainer.dispatchEvent(new MouseEvent('click', { bubbles: true }));

        return;
      }

      if (removeBtn) {
        const setContainer = familySetRoot.querySelector('.family-set__set-container');
        if (!setContainer) return;

        // remove variant from bundle
        const storedVariant = setContainer.dataset.variantId;
        if (storedVariant) {
          bundle.items = bundle.items.filter(l => String(l.id) !== String(storedVariant));
          updateBundleCtaTotal();
        }

        const allSets = document.querySelectorAll('.family-set');
        const isOnlySet = allSets.length === 1;

        if (isOnlySet) {
          // reset UI if this is the only set
          setContainer.querySelectorAll('.active').forEach(el => el.classList.remove('active'));
          setContainer.querySelector('.family-set__footer')?.classList.add('hidden');

          const titleSpan = familySetRoot.querySelector('[data-original-title]');
          const priceSpan = familySetRoot.querySelector('.family-set__set-total-price');
          const numSpan   = familySetRoot.querySelector('.family-set__set-number');
          if (titleSpan) titleSpan.textContent = titleSpan.getAttribute('data-original-title');
          priceSpan?.classList.add('hidden');
          if (priceSpan) priceSpan.textContent='';
          numSpan?.classList.remove('hidden');

          delete setContainer.dataset.variantId;
          delete setContainer.dataset.quantity;
          delete setContainer.dataset.productId;

        } else {
          // delete the whole block
          familySetRoot.parentNode.removeChild(familySetRoot);

          // renumber remaining sets
          updateFamilySetNumbers();
        }
      }
    });
  }

  // --- Event listener binding per set block ---------------------------------
  function setupEventListenersForSet(setRoot) {
    // Accessibility roles for quantity steppers
    setRoot.querySelectorAll('.family-set__set-quantity-minus, .family-set__set-quantity-plus')
      .forEach(btn => {
        btn.setAttribute('role', 'button');
        if (btn.classList.contains('family-set__set-quantity-minus')) {
          btn.setAttribute('aria-label', 'Decrease quantity');
        } else {
          btn.setAttribute('aria-label', 'Increase quantity');
        }
        btn.setAttribute('tabindex', '0');
      });

    setRoot.addEventListener('click', (e) => {

      const tileBtn = e.target.closest('.family-set__item');
      if (tileBtn && setRoot.contains(tileBtn)) {
        handleStep1Select(setRoot, tileBtn);
      }

      const sizeBtn = e.target.closest('.family-set__size-item');
      if (sizeBtn && setRoot.contains(sizeBtn)) {
        const step2El = sizeBtn.closest('.family-set__step-2');
        handleSizeSelect(step2El, sizeBtn);
      }

      const addSetBtn = e.target.closest('.family-set__add-set-to-bundle');
      if (addSetBtn && setRoot.contains(addSetBtn)) {
        // Button might live outside the .family-set__step-2 block. Try closest first, then fallback to the active one.
        let step2El = addSetBtn.closest('.family-set__step-2');
        if (!step2El) {
          step2El = setRoot.querySelector('.family-set__step-2.active');
        }
        // Final fallback: find any step-2 that currently has a selected size
        if (!step2El) {
          step2El = setRoot.querySelector('.family-set__step-2 .family-set__size-item.active')?.closest('.family-set__step-2') || null;
        }
        if (!step2El) {
          console.warn('[FamilySet] Could not determine step-2 element for Add Set action');
        } else {
          addConfiguredLineToBundle(step2El);
        }
      }

      const addAnotherBtn = e.target.closest('.family-set__add-another-set');
      if (addAnotherBtn) {
        // validation: must have at least one configured set before cloning new
        if (!bundle.items.length) {
          console.warn('[FamilySet] Bundle is empty, cannot clone another set');
          const valEl = addAnotherBtn.parentElement.querySelector('.family-set__another-set-error');
          if (valEl) {
            valEl.classList.remove('hidden');
            setTimeout(()=> valEl.classList.add('hidden'), 4000);
          }
          return;
        }
        const familySetRoot = setRoot.closest('.family-set');
        if (familySetRoot) cloneFamilySetBlock(familySetRoot);
      }

      const triggerBtn = e.target.closest('.family-set__add-bundle-to-bag');
      if (triggerBtn && setRoot.contains(triggerBtn)) {
        postBundleToCart(triggerBtn);
      }

      // Quantity +/- handlers
      const minusBtn = e.target.closest('.family-set__set-quantity-minus');
      const plusBtn  = e.target.closest('.family-set__set-quantity-plus');
      if ((minusBtn || plusBtn) && setRoot.contains(e.target)) {
        const qtyInput = setRoot.querySelector('.family-set__set-quantity-input');
        if (!qtyInput) return;
        let current = parseInt(qtyInput.value, 10) || 1;
        const min = parseInt(qtyInput.min || '1', 10);
        const max = qtyInput.max ? parseInt(qtyInput.max, 10) : Infinity;

        if (minusBtn) {
          current = Math.max(min, current - 1);
        } else {
          current = Math.min(max, current + 1);
        }
        qtyInput.value = current;

        // Recalculate total price based on quantity and unit price
        const activeSize = setRoot.querySelector('.family-set__size-item.active');
        if (activeSize) {
          const unitPriceText = activeSize.getAttribute('data-price') || '';
          const currencySymbol = unitPriceText.replace(/[0-9.,]/g, '').trim() || '£';
          const numeric = parseFloat(unitPriceText.replace(/[^0-9.]/g, ''));

          if (!isNaN(numeric)) {
            const totalVal = numeric * current;
            const totalText = totalVal % 1 === 0 ? totalVal.toFixed(0) : totalVal.toFixed(2);
            const formatted = `${currencySymbol}${totalText}`;

            // update footer price
            const priceSpan = setRoot.querySelector('.family-set__set-price');
            if (priceSpan) priceSpan.innerHTML = formatted;

            // update header price if visible
            const headerPriceSpan = setRoot.closest('.family-set').querySelector('.family-set__set-total-price');
            if (headerPriceSpan && !headerPriceSpan.classList.contains('hidden')) {
              headerPriceSpan.innerHTML = `&nbsp;- ${formatted}`;
            }
            // also store latest price into dataset for later save
            activeSize.setAttribute('data-price-total', formatted);
          }
        }
      }
    });
  }

  // --- Utility: refresh visible numbering of each family-set block -----------
  function updateFamilySetNumbers() {
    document.querySelectorAll('.family-set__set-number').forEach((el, idx) => {
      el.textContent = idx + 1;
    });
  }

  // --- Utility to generate unique IDs for cloned elements ------------------------
  function refreshUniqueIds(rootEl) {
    const idMap = new Map();
    rootEl.querySelectorAll('[id]').forEach((el) => {
      // skip elements inside SVGs
      if (el.ownerSVGElement) return;
      const oldId = el.id;
      const newId = `${oldId}--${Date.now()}-${Math.floor(Math.random()*1000)}`;
      el.id = newId;
      idMap.set(oldId, newId);
    });
    // Update labels referencing old IDs
    rootEl.querySelectorAll('label[for]').forEach((lab) => {
      const ref = lab.getAttribute('for');
      if (idMap.has(ref)) {
        lab.setAttribute('for', idMap.get(ref));
      }
    });
  }

  // --- Helper: detect half-configured set (active but not saved) -------------
  // -- should be when step-2 is visible but not added to the bundle
  // -- or when the footer is visible
  function hasHalfConfiguredSet () {
    return !!document.querySelector(
      '.family-set__set-container:not([data-variant-id]) :is(' +
        '.family-set__item.active,' +          // step-1 product chosen
        '.family-set__step-2.active,' +        // step-2 container open
        '.family-set__size-item.active,' +     // size chosen
        '.family-set__footer:not(.hidden)' +   // footer showing
      ')'
    );
  }

  function updateAddAnotherBtn() {
    const btn = document.querySelector('.family-set__add-another-set');
    if (!btn) return;
    if (bundle.items.length === 0) {
      btn.classList.add('disabled');
    } else {
      btn.classList.remove('disabled');
    }
  }

  // --- Initialise on DOM ready ----------------------------------------------
  function setupEventListeners() {
    const familySetRoots = document.querySelectorAll('.family-set');
    if (!familySetRoots.length) return;

    familySetRoots.forEach(root => setupListenersForFamilySet(root));

    // Fallback global listener in case the button sits completely outside any .family-set
    document.addEventListener('click', (e) => {
      const triggerBtn = e.target.closest('.family-set__add-bundle-to-bag');
      if (triggerBtn) {
        postBundleToCart(triggerBtn);
      }
    });

    // Add global add-another-set button listener
    document.addEventListener('click', (e) => {
      const addAnotherBtn = e.target.closest('.family-set__add-another-set');
      if (addAnotherBtn) {
        if (hasHalfConfiguredSet()) {
          document.querySelector('.family-set__update-set-error')?.classList.remove('hidden');
          setTimeout(()=>document.querySelector('.family-set__update-set-error')?.classList.add('hidden'),4000);
          return;
        }
        if (!bundle.items.length) {
          console.warn('[FamilySet] Bundle is empty, cannot clone another set');
          const valEl = addAnotherBtn.parentElement.querySelector('.family-set__another-set-error');
          if (valEl) {
            valEl.classList.remove('hidden');
            setTimeout(()=> valEl.classList.add('hidden'), 4000);
          }
          return;
        }
        const familySetRoot = document.querySelector('.family-set'); // Assuming the first family-set is the one to clone
        if (familySetRoot) {
          cloneFamilySetBlock(familySetRoot);
          addAnotherBtn.classList.add('disabled');
        }
      }
    });

    // Initial numbering
    updateFamilySetNumbers();
    updateAddAnotherBtn();
  }

  return { setupEventListeners };
})();

document.addEventListener('DOMContentLoaded', () => {
  FamilySet.setupEventListeners();
});

/* -------------------------------------------------------------
   Delegated listener — works for every current & future trigger
------------------------------------------------------------- */
if (!window.__sizeGuideDelegated) {
  window.__sizeGuideDelegated = true;

  document.addEventListener('click', (e) => {
    const trg = e.target.closest('.product-form__size-guide-trigger, .size-guide-trigger');
    if (!trg) return;                          // not a size-guide link

    const modal = document.querySelector('size-guide-modal');
    if (modal && typeof modal.open === 'function') {
      modal.open();
    }
  });
}
